// Secure download handler for PPO ZIPs via API (adds x-user-id header)
window.secureDownloadPpo = function(link, event, id) {
  event.preventDefault();
  const user = localStorage.getItem('cdco38_user');
  if (!user) {
    alert('Vous devez être connecté pour télécharger.');
    return false;
  }
  const userId = JSON.parse(user).id;
  fetch(link.href, {
    headers: { 'x-user-id': userId }
  })
    .then(res => {
      if (!res.ok) throw new Error('Erreur lors du téléchargement.');
      return res.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Force filename to ppo_{id}.zip
      a.download = `ppo_${id}.zip`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        a.remove();
      }, 100);
    })
    .catch(err => alert(err.message));
  return false;
};
import './login.js';
// Pre-scan all SVGs in /maps/*/*.svg at build time for fast lookup (Vite limitation workaround)
// (No longer using import.meta.glob for maps images; use manifest instead)
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import OSM from 'ol/source/OSM';
import Static from 'ol/source/ImageStatic';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import proj4 from 'proj4';
import {register} from 'ol/proj/proj4';

// Correct RGF93 / Lambert 93 definition (y_0 = 6000000)
proj4.defs('EPSG:2154', '+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');

// Register proj4 so OpenLayers can transform EPSG:2154
register(proj4);





// Helper to get all jpg/jgw pairs from the maps-manifest.json in public
async function getJpgJgwPairsFromMaps() {
  // Fetch the manifest
  const res = await fetch('/maps-manifest.json');
  if (!res.ok) throw new Error('Failed to load maps-manifest.json');
  const files = await res.json();

  // Group files by folder and base name
  const pngs = {};
  const jgws = {};
  for (const file of files) {
    if (file.endsWith('.png')) {
      const match = file.match(/^maps\/([^/]+)\/(.+)\.png$/i);
      if (match) {
        const folder = match[1];
        const base = match[2];
        pngs[`${folder}/${base}`] = `/${file}`;
      }
    } else if (file.endsWith('.jgw')) {
      const match = file.match(/^maps\/([^/]+)\/(.+)\.jgw$/i);
      if (match) {
        const folder = match[1];
        const base = match[2];
        jgws[`${folder}/${base}`] = `/${file}`;
      }
    }
  }

  // Pair png and jgw by folder/base
  const pairs = [];
  for (const key of Object.keys(pngs)) {
    if (jgws[key]) {
      pairs.push({ jpg: pngs[key], jgw: jgws[key] });
    }
  }
  return pairs;
}


async function loadJgw(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to load JGW: ' + res.statusText);
  const text = await res.text();
  const lines = text.trim().split(/\r?\n/).map(l => l.trim());
  if (lines.length < 6) throw new Error('Invalid JGW file');
  return {
    pixelWidth: parseFloat(lines[0]),
    rotationX: parseFloat(lines[1]),
    rotationY: parseFloat(lines[2]),
    pixelHeight: parseFloat(lines[3]),
    topLeftX: parseFloat(lines[4]),
    topLeftY: parseFloat(lines[5])
  };
}

function getImageDimensions(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to load image: ' + url));
    img.src = url;
  });
}


async function initMap() {
  try {
    // Fetch cartes, carteType, and carteStatut from backend API
    const [cartesRows, carteTypes, carteStatuts] = await Promise.all([
      fetch('/api/cartes').then(r => r.json()),
      fetch('/api/cartetypes').then(r => r.json()),
      fetch('/api/cartestatuts').then(r => r.json())
    ]);
    const carteTypeMap = Object.fromEntries(carteTypes.map(({id, label}) => [id, label]));
    const carteStatutMap = Object.fromEntries(carteStatuts.map(({id, label}) => [id, label]));
    // Define cartesFields from the first row of cartesRows
    const cartesFields = Object.keys(cartesRows[0] || {});

    const pairs = await getJpgJgwPairsFromMaps();
    if (!pairs.length) throw new Error('No image pairs found in maps/');

    // Add loading bar
    let loadingBar = document.createElement('div');
    loadingBar.id = 'loading-bar';
    loadingBar.style.position = 'fixed';
    loadingBar.style.top = '0';
    loadingBar.style.left = '0';
    loadingBar.style.width = '100vw';
    loadingBar.style.height = '6px';
    loadingBar.style.background = '#eee';
    loadingBar.style.zIndex = '3000';
    loadingBar.innerHTML = '<div style="height:100%;width:0;background:#1a237e;transition:width 0.2s;" id="loading-bar-progress"></div>';
    document.body.appendChild(loadingBar);

    // Build jpgLookup: folderName -> first .png path in that folder
    const jpgLookup = {};
    for (const pair of pairs) {
      const match = pair.jpg.match(/^\/maps\/([^/]+)\/(.+)\.png$/i);
      if (match) {
        const folder = match[1];
        if (!jpgLookup[folder]) {
          jpgLookup[folder] = `maps/${folder}/${match[2]}.png`;
        }
      }
    }

    console.log(`Found ${pairs.length} image pairs.`);
    const layers = [
      new TileLayer({
        source: new OSM({ attributions: [] }),
        opacity: 0.5
      }),
      // Add vector layer for Isère borders
      new VectorLayer({
        source: new VectorSource({
          url: '/departement-38-isere.geojson',
          format: new GeoJSON()
        }),
        style: new Style({
          stroke: new Stroke({
            color: '#000000',
            width: 5
          })
        }),
        visible: true
      })
    ];
    let imageLayerInfo = [];
    // Loading bar progress variables
    const loadingBarProgress = document.getElementById('loading-bar-progress');
    let loadedCount = 0;
    const totalToLoad = pairs.length;
    for (const pair of pairs) {

      // Extract the ID from the pair filename (first number in the filename)
      // remove "/maps/" prefix
      const idMatch = pair.jpg.replace(/^\/maps\//, '').match(/^(\d+)/); 
      let row = null;
      if (idMatch) {
        const folderId = parseInt(idMatch[1], 10);
        row = cartesRows.find(r => parseInt(r.ID, 10) === folderId);
      }
      // If no matching row found, skip this image
      if (!row) {
        console.warn('No matching database row found for image:', pair.jpg);
        continue;
      }
      
      // Skip if statut is 'Obsolète'
      const statutLabel = carteStatutMap[row.CarteStatutId] || '';
      if (statutLabel === 'Obsolète') {
        continue;
      }

      const imageUrl = pair.jpg;
      const jgwUrl = pair.jgw;
      try {
        const [jgw, { width: imageWidth, height: imageHeight }] = await Promise.all([
          loadJgw(jgwUrl),
          getImageDimensions(imageUrl)
        ]);

        const pw = jgw.pixelWidth;
        const ph = jgw.pixelHeight;
        const tlx = jgw.topLeftX;
        const tly = jgw.topLeftY;

        const minX = tlx - pw / 2;
        const maxX = tlx + pw * (imageWidth - 0.5);
        const maxY = tly + (-ph) / 2;
        const minY = tly + ph * (imageHeight - 0.5);

        const rgf93Extent = [minX, minY, maxX, maxY];

        // Get subdir name for label (just the folder name)
        const labelMatch = imageUrl.match(/\/maps\/([^/]+)\//);
        let label = labelMatch ? labelMatch[1] : imageUrl;
        try {
          label = decodeURIComponent(label);
        } catch {}
        const layer = new ImageLayer({
          source: new Static({ url: imageUrl, projection: 'EPSG:2154', imageExtent: rgf93Extent }),
          visible: true,
          label: label
        });
        // Calculate area for sorting
        const area = Math.abs((rgf93Extent[2] - rgf93Extent[0]) * (rgf93Extent[3] - rgf93Extent[1]));
        imageLayerInfo.push({ layer, extent: rgf93Extent, label, area });
      } catch (err) {
        console.error('Failed to load image or JGW for', imageUrl, err);
      }
      loadedCount++;
      if (loadingBarProgress) {
        loadingBarProgress.style.width = ((loadedCount / totalToLoad) * 100) + '%';
      }
    }
    // Remove loading bar when done
    if (loadingBar) {
      setTimeout(() => loadingBar.remove(), 500);
    }

        // Sort imageLayerInfo by map number (DESC) extracted from label
    // Sort imageLayerInfo by area (largest to smallest)
    imageLayerInfo.sort((a, b) => b.area - a.area);
    // Rebuild layers array: OSM first, then sorted image layers
    const osmLayer = layers[0];
    const borderLayer = layers[1];
    layers.length = 0;
    layers.push(osmLayer);
    layers.push(borderLayer);
    imageLayerInfo.forEach(({ layer }) => layers.push(layer));
    imageLayerInfo.sort((a, b) => b.label.localeCompare(a.label)); // Sort back by label (folder name) DESC for the legend

    const map = new Map({
      target: 'map',
      layers,
      view: new View({
        center: [913789.572, 6457154.288],
        projection: 'EPSG:2154',
        zoom: 10,
        constrainRotation: true // Disable rotation (including touch gestures)
      })
    });

    // Add layer selection UI (sidebar)
    const controlsDiv = document.getElementById('layer-controls');
    controlsDiv.innerHTML = `
      <div id="layer-controls-header" style="display:flex;align-items:center;justify-content:space-between;">
        <h3 style="margin:0;">Cartes</h3>
        <button id="toggle-layers-menu" style="font-size:1.2em;padding:2px 10px;border-radius:6px;border:1px solid #bbb;background:#f5f5f5;cursor:pointer;">☰</button>
      </div>
      <div id="layer-controls-content"></div>
    `;
    const contentDiv = document.getElementById('layer-controls-content');
    // All/None/Scolaires toggle
    const allNoneDiv = document.createElement('div');
    allNoneDiv.style.marginBottom = '8px';
    const allBtn = document.createElement('button');
    allBtn.textContent = 'All';
    allBtn.style.marginRight = '6px';
    allBtn.style.padding = '2px 10px';
    allBtn.style.borderRadius = '6px';
    allBtn.style.border = '1px solid #bbb';
    allBtn.style.background = '#e0f7fa';
    allBtn.style.cursor = 'pointer';
    const noneBtn = document.createElement('button');
    noneBtn.textContent = 'None';
    noneBtn.style.marginRight = '6px';
    noneBtn.style.padding = '2px 10px';
    noneBtn.style.borderRadius = '6px';
    noneBtn.style.border = '1px solid #bbb';
    noneBtn.style.background = '#ffe0e0';
    noneBtn.style.cursor = 'pointer';
    const scolairesBtn = document.createElement('button');
    scolairesBtn.textContent = 'Scolaires';
    scolairesBtn.style.padding = '2px 10px';
    scolairesBtn.style.borderRadius = '6px';
    scolairesBtn.style.border = '1px solid #bbb';
    scolairesBtn.style.background = '#e0ffe0';
    scolairesBtn.style.cursor = 'pointer';

    // VTT, Pied, Ski buttons
    const vttBtn = document.createElement('button');
    vttBtn.textContent = 'VTT';
    vttBtn.style.marginLeft = '6px';
    vttBtn.style.padding = '2px 10px';
    vttBtn.style.borderRadius = '6px';
    vttBtn.style.border = '1px solid #bbb';
    vttBtn.style.background = '#e0e0ff';
    vttBtn.style.cursor = 'pointer';

    const piedBtn = document.createElement('button');
    piedBtn.textContent = 'Pied';
    piedBtn.style.marginLeft = '6px';
    piedBtn.style.padding = '2px 10px';
    piedBtn.style.borderRadius = '6px';
    piedBtn.style.border = '1px solid #bbb';
    piedBtn.style.background = '#fffbe0';
    piedBtn.style.cursor = 'pointer';

    const skiBtn = document.createElement('button');
    skiBtn.textContent = 'Ski';
    skiBtn.style.marginLeft = '6px';
    skiBtn.style.padding = '2px 10px';
    skiBtn.style.borderRadius = '6px';
    skiBtn.style.border = '1px solid #bbb';
    skiBtn.style.background = '#e0f7ff';
    skiBtn.style.cursor = 'pointer';

    // Helper to set active button style
    function setActiveFilterBtn(activeBtn) {
      [allBtn, noneBtn, scolairesBtn, vttBtn, piedBtn, skiBtn].forEach(btn => {
        if (btn === activeBtn) {
          btn.classList.add('active-filter-btn');
          btn.style.fontWeight = 'bold';
        } else {
          btn.classList.remove('active-filter-btn');
          btn.style.fontWeight = '';
        }
      });
    }
    allNoneDiv.appendChild(allBtn);
    allNoneDiv.appendChild(noneBtn);
    allNoneDiv.appendChild(scolairesBtn);
    allNoneDiv.appendChild(vttBtn);
    allNoneDiv.appendChild(piedBtn);
    allNoneDiv.appendChild(skiBtn);
    contentDiv.appendChild(allNoneDiv);
    // Layer checkboxes
    imageLayerInfo.forEach(({ layer, label }, idx) => {
      const id = `layer-checkbox-${idx}`;
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = id;
      checkbox.checked = true;
      checkbox.onchange = () => {
        layer.setVisible(checkbox.checked);
      };
      const lbl = document.createElement('label');
      lbl.htmlFor = id;
      lbl.textContent = label;
      const div = document.createElement('div');
      div.appendChild(checkbox);
      div.appendChild(lbl);
      contentDiv.appendChild(div);
    });
    // All/None/Scolaires button logic
    allBtn.onclick = () => {
      setActiveFilterBtn(allBtn);
      imageLayerInfo.forEach(({ layer }, idx) => {
        layer.setVisible(true);
        document.getElementById(`layer-checkbox-${idx}`).checked = true;
      });
    };
    noneBtn.onclick = () => {
      setActiveFilterBtn(noneBtn);
      imageLayerInfo.forEach(({ layer }, idx) => {
        layer.setVisible(false);
        document.getElementById(`layer-checkbox-${idx}`).checked = false;
      });
    };
    scolairesBtn.onclick = () => {
      setActiveFilterBtn(scolairesBtn);
      imageLayerInfo.forEach(({ layer }, idx) => {
        // Extract the ID from the label (first number in the label)
        const idMatch = layer.get('label').match(/^(\d+)/);
        let row = null;
        if (idMatch) {
          const folderId = parseInt(idMatch[1], 10);
          row = cartesRows.find(r => parseInt(r.ID, 10) === folderId);
        }
        // Show only if Scolaires == 1 / 'Oui'
        if (row && (row.Scolaires === 1)) {
          layer.setVisible(true);
          document.getElementById(`layer-checkbox-${idx}`).checked = true;
        } else {
          layer.setVisible(false);
          document.getElementById(`layer-checkbox-${idx}`).checked = false;
        }
      });
    };
    piedBtn.onclick = () => {
      setActiveFilterBtn(piedBtn);
      imageLayerInfo.forEach(({ layer }, idx) => {
        // Extract the ID from the label (first number in the label)
        const idMatch = layer.get('label').match(/^(\d+)/);
        let row = null;
        if (idMatch) {
          const folderId = parseInt(idMatch[1], 10);
          row = cartesRows.find(r => parseInt(r.ID, 10) === folderId);
        }
        // Show only if Type == 'Pied'
        if (row && ((carteTypeMap[row.CarteTypeId] === 'CO à pied') ||
          (carteTypeMap[row.CarteTypeId] === 'Rand\'O') ||
          (carteTypeMap[row.CarteTypeId] === 'Raid\'O'))) {
          layer.setVisible(true);
          document.getElementById(`layer-checkbox-${idx}`).checked = true;
        } else {
          layer.setVisible(false);
          document.getElementById(`layer-checkbox-${idx}`).checked = false;
        }
      });
    };
    skiBtn.onclick = () => {
      setActiveFilterBtn(skiBtn);
      imageLayerInfo.forEach(({ layer }, idx) => {
        // Extract the ID from the label (first number in the label)
        const idMatch = layer.get('label').match(/^(\d+)/);
        let row = null;
        if (idMatch) {
          const folderId = parseInt(idMatch[1], 10);
          row = cartesRows.find(r => parseInt(r.ID, 10) === folderId);
        }
        // Show only if Type == 'Ski'
        if (row && (carteTypeMap[row.CarteTypeId] === 'CO à ski')) {
          layer.setVisible(true);
          document.getElementById(`layer-checkbox-${idx}`).checked = true;
        } else {
          layer.setVisible(false);
          document.getElementById(`layer-checkbox-${idx}`).checked = false;
        }
      });
    };
    vttBtn.onclick = () => {
      setActiveFilterBtn(vttBtn);
      imageLayerInfo.forEach(({ layer }, idx) => {
        // Extract the ID from the label (first number in the label)
        const idMatch = layer.get('label').match(/^(\d+)/);
        let row = null;
        if (idMatch) {
          const folderId = parseInt(idMatch[1], 10);
          row = cartesRows.find(r => parseInt(r.ID, 10) === folderId);
        }
        // Show only if Type == 'VTT'
        if (row && (carteTypeMap[row.CarteTypeId] === 'CO à VTT')) {
          layer.setVisible(true);
          document.getElementById(`layer-checkbox-${idx}`).checked = true;
        } else {
          layer.setVisible(false);
          document.getElementById(`layer-checkbox-${idx}`).checked = false;
        }
      });
    };
    // Hidable menu for mobile
    let menuVisible = true;
    const toggleBtn = document.getElementById('toggle-layers-menu');
    toggleBtn.onclick = () => {
      menuVisible = !menuVisible;
      contentDiv.style.display = menuVisible ? '' : 'none';
    };
    // Start hidden on small screens
    if (window.innerWidth < 600) {
      menuVisible = false;
      contentDiv.style.display = 'none';
    }

    piedBtn.click(); // Show Pied maps by default

    // Add hover popup for each layer, showing all info from cartes table
    const popup = document.createElement('div');
    popup.style.position = 'absolute';
    popup.style.pointerEvents = 'auto';
    popup.style.background = 'rgba(255,255,255,0.97)';
    popup.style.border = '1px solid #888';
    popup.style.padding = '8px 12px';
    popup.style.borderRadius = '6px';
    popup.style.fontSize = '14px';
    popup.style.display = 'none';
    popup.style.zIndex = '1000';
    popup.style.maxWidth = '350px';
    popup.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    popup.tabIndex = 0;
    document.body.appendChild(popup);

    let popupPinned = false;
    popup.addEventListener('mouseenter', () => {
      popupPinned = true;
    });
    popup.addEventListener('mouseleave', () => {
      popupPinned = false;
      popup.style.display = 'none';
    });

    map.on('pointermove', async function(evt) {
      if (popupPinned) return;
      const pixel = evt.pixel;
      const coord = evt.coordinate;
      let found = false;
      // Loop in reverse order to prioritize top-most layer
      for (let i = imageLayerInfo.length - 1; i >= 0; i--) {
        const info = imageLayerInfo[i];
        if (!info.layer.getVisible()) continue;
        const [minX, minY, maxX, maxY] = info.extent;
        if (
          coord[0] >= minX && coord[0] <= maxX &&
          coord[1] >= minY && coord[1] <= maxY
        ) {
          // Try to match info.label to a row in cartesRows (by NomCarte or similar)
          let row = null;
          if (cartesRows && cartesRows.length) {
            // Match by ID: extract first number from info.label (folder name)
            const idMatch = info.label.match(/^(\d+)/);
            if (idMatch) {
              const folderId = parseInt(idMatch[1], 10);
              row = cartesRows.find(r => parseInt(r.ID, 10) === folderId);
            }
          }
          let html = `<strong>${info.label}</strong>`;
          if (row) {
            html += '<br><table style="margin-top:2px;font-size:12px;line-height:1.2;">';
            // Get user rights from localStorage
            let userRights = -1;
            try {
              const user = localStorage.getItem('cdco38_user');
              if (user) {
                const parsed = JSON.parse(user);
                userRights = typeof parsed.rights === 'number' ? parsed.rights : -1;
              }
            } catch {}

            for (const f of cartesFields) {
              if (row[f] == null || row[f] === '' || f.toLowerCase() === 'folder') continue;
              let value = row[f];
              let label = f;
                if (f === 'CarteStatutId' && carteStatutMap[value] !== undefined) {
                  value = carteStatutMap[value];
                  label = 'Statut';
                  // Add color coding for Statut
                  let color = '#c00'; // default red
                  if (value === 'OK') color = '#2ecc40'; // green
                  value = `<span style="color:${color};font-weight:bold;">${value}</span>`;
                } else if (f === 'CarteTypeId' && carteTypeMap[value] !== undefined) {
                  value = carteTypeMap[value];
                  label = 'Type';
                } else if (f === 'glink') {
                  // Only show GDrive if rights >= Club (2)
                  if (userRights >= 2) {
                    label = 'GDrive';
                    value = `<a href=\"${value}" target=\"_blank\" rel=\"noopener noreferrer\">Lien</a>`;
                    html += `<tr><td style="color:#555;padding-right:8px;">${label}</td><td>${value}</td></tr>`;
                  }
                  continue;
                } else if (f === 'FFCO-Cartographe') {
                  label = 'Cartographe';
                } else if (f.toLowerCase() === 'echelle') {
                  value = `1:${value}`;
                } else if (f === 'PPO') {
                  const isOui = value === 1 || value === '1' || value === 'Oui';
                  if (isOui && userRights >= 1) {
                    // Add secure PPO zip download link
                    html += `<tr><td style="color:#555;padding:2px 6px 2px 0;vertical-align:top;">PPO</td><td style="padding:2px 0 2px 0;vertical-align:top;"><a href="/api/download-ppo-zip/${row.ID}" onclick="return window.secureDownloadPpo && window.secureDownloadPpo(this, event, ${row.ID});">Fichiers</a></td></tr>`;
                    continue;
                  } else {
                    value = isOui ? 'Oui' : 'Non';
                  }
                } else if (f === 'Scolaires') {
                  value = value === 1 || value === '1' ? 'Oui' : 'Non';
                } else if (f === 'MiseAJour') {
                  label = 'Mise à jour';
                } else if (f === 'NomCarte') {
                  label = 'Nom de la carte';
                }
                if (f !== 'glink') {
                  html += `<tr><td style="color:#555;padding:2px 6px 2px 0;vertical-align:top;">${label}</td><td style="padding:2px 0 2px 0;vertical-align:top;">${value}</td></tr>`;
                }
            }
            // Add JPG 300dpi field with download link only if rights >= Scolaire (1) AND carte is allowed for Scolaires
            const folderName = info.label;
            const jpgPath = jpgLookup[folderName] || '';
            // Check if carte is allowed for Scolaires (assume field is named 'Scolaires' and value 1 or '1' means allowed)
            const isScolairesAllowed = (row.Scolaires === 1);
            if (((userRights == 1 && isScolairesAllowed) || userRights > 1) && (carteStatutMap[row.CarteStatutId] === 'OK')) {
              if (jpgPath) {
                // Extract folder and filename from jpgPath (maps/{folder}/{filename}.png)
                const match = jpgPath.match(/^maps\/([^/]+)\/([^/]+\.png)$/);
              if (match) {
                const folder = encodeURIComponent(match[1]);
                  let filename = match[2];
                  // Force extension to .jpg
                  filename = filename.replace(/\.png$/i, '.jpg');
                filename = encodeURIComponent(filename);
                  html += `<tr><td style="color:#555;padding:2px 6px 2px 0;vertical-align:top;">JPG 300dpi</td><td style="padding:2px 0 2px 0;vertical-align:top;">
                    <a href="/api/download-jpg/${row.ID}" class="jpg300dpi-link" onclick="return window.secureDownloadJpg && window.secureDownloadJpg(this, event);">Télécharger</a>
                  </td></tr>`;
                } else {
                  html += `<tr><td style="color:#555;padding:2px 6px 2px 0;vertical-align:top;">JPG 300dpi</td><td style="padding:2px 0 2px 0;vertical-align:top;">
                    <span style="color:#c00; opacity:0.7;">Lien invalide</span>
                  </td></tr>`;
            }
              } else {
                html += `<tr><td style="color:#555;padding:2px 6px 2px 0;vertical-align:top;">JPG 300dpi</td><td style="padding:2px 0 2px 0;vertical-align:top;">
                    <span style="color:#c00; opacity:0.7;">Aucun JPG trouvé</span>
                  </td></tr>`;
              }
            } else if (userRights == 1 && !isScolairesAllowed) {
              html += `<tr><td style="color:#555;padding:2px 6px 2px 0;vertical-align:top;">JPG 300dpi</td><td style="padding:2px 0 2px 0;vertical-align:top;">
                    <span style="color:#c00; opacity:0.7;">Non autorisé</span>
                  </td></tr>`;
            }
            // Secure download handler for JPGs via API (adds x-user-id header)
            window.secureDownloadJpg = function(link, event) {
              event.preventDefault();
              const user = localStorage.getItem('cdco38_user');
              if (!user) {
                alert('Vous devez être connecté pour télécharger.');
                return false;
              }
              const userId = JSON.parse(user).id;
              fetch(link.href, {
                headers: { 'x-user-id': userId }
              })
                .then(res => {
                  if (!res.ok) throw new Error('Erreur lors du téléchargement.');
                  return res.blob();
                })
                .then(blob => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  // Force filename to {id}.jpg
                  let id = link.href.split('/').pop();
                  a.download = id + '.jpg';
                  document.body.appendChild(a);
                  a.click();
                  setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                    a.remove();
                  }, 100);
                })
                .catch(err => alert(err.message));
              return false;
            };
            html += '</table>';
          }
          // Add Cacher button
          html += `<div style="margin-top:8px;text-align:right;"><button id="hide-layer-btn" style="padding:2px 10px;border-radius:6px;border:1px solid #bbb;background:#eee;cursor:pointer;">Cacher</button></div>`;
          popup.innerHTML = html;
          popup.style.left = evt.originalEvent.clientX + 10 + 'px';
          popup.style.top = evt.originalEvent.clientY + 10 + 'px';
          popup.style.display = 'block';
          // Add event for Cacher button
          setTimeout(() => {
            const hideBtn = document.getElementById('hide-layer-btn');
            if (hideBtn) {
              hideBtn.onclick = () => {
                info.layer.setVisible(false);
                // Update the legend checkbox
                const idx = imageLayerInfo.findIndex(l => l.label === info.label);
                if (idx !== -1) {
                  const cb = document.getElementById(`layer-checkbox-${idx}`);
                  if (cb) cb.checked = false;
                }
                popup.style.display = 'none';
              };
            }
          }, 0);
          found = true;
          break;
        }
      }
      if (!found && !popupPinned) {
        popup.style.display = 'none';
      }
    });

    // Hide popup if mouse leaves map and not over popup
    const mapDiv = document.getElementById('map');
    mapDiv.addEventListener('mouseleave', () => {
      setTimeout(() => {
        if (!popupPinned) popup.style.display = 'none';
      }, 100);
    });
  } catch (err) {
    console.error('Failed to initialize map:', err);
  }
}

initMap();
